import { sectionInnerNames } from 'features/prompts/lib/promptDraft';
import React, { useMemo, useState } from 'react';
import { X, Copy, Check, ExternalLink } from 'lucide-react';
import { drawerIconButtonClass } from 'features/activity/styles/dataTableStyles';
import DrawerTab from 'shared/components/ui/DrawerTab';

/**
 * "Use this prompt" — every step a caller actually has to write, generated from
 * what THIS prompt version contains.
 *
 * Two blocks: what you write once, and what you write per call. The second is a
 * single block rather than a step list because that is how it lands in a
 * codebase — one function, not five snippets to stitch together.
 *
 * Contents are dynamic: a prompt with no variables shows a compile() with no
 * arguments, and the comment block names only what this version actually pins.
 *
 * `selector` describes exactly what's loaded in the editor, so the snippet
 * fetches the SAME thing you're looking at — not always `production`:
 *   { kind: 'label', value: 'production' | ... }  |  { kind: 'hash', value: '…' }
 */

const DOCS_URL = 'https://docs.trodo.ai/prompts/quickstart';

// ── syntax highlighting ──────────────────────────────────────────────────────
// A deliberately small tokenizer. Pulling in a highlighting library to colour
// two twenty-line snippets is not a trade worth making, and the grammar here is
// only ever OUR generated code — no user input reaches it.
const KEYWORDS = {
  js: /\b(import|from|const|let|await|async|function|return|new|if|else|for|of|export|default)\b/g,
  py: /\b(import|from|def|return|if|else|for|in|with|as|None|True|False|print)\b/g,
};

function highlight(code, lang) {
  const parts = [];
  const commentRe = lang === 'py' ? /#.*$/gm : /\/\/.*$/gm;
  const stringRe = /(['"`])(?:\\.|(?!\1)[^\\])*\1/g;

  // Mark comments and strings first so keywords inside them stay plain.
  const marks = [];
  let m;
  while ((m = commentRe.exec(code)) !== null) marks.push({ s: m.index, e: m.index + m[0].length, t: 'comment' });
  while ((m = stringRe.exec(code)) !== null) {
    const at = m.index;
    const len = m[0].length;
    if (!marks.some((k) => at >= k.s && at < k.e)) marks.push({ s: at, e: at + len, t: 'string' });
  }
  marks.sort((a, b) => a.s - b.s);

  let cursor = 0;
  const pushPlain = (text) => {
    let last = 0;
    const kw = KEYWORDS[lang];
    kw.lastIndex = 0;
    let k;
    while ((k = kw.exec(text)) !== null) {
      if (k.index > last) parts.push({ t: 'plain', v: text.slice(last, k.index) });
      parts.push({ t: 'kw', v: k[0] });
      last = k.index + k[0].length;
    }
    if (last < text.length) parts.push({ t: 'plain', v: text.slice(last) });
  };

  for (const mk of marks) {
    if (mk.s > cursor) pushPlain(code.slice(cursor, mk.s));
    parts.push({ t: mk.t, v: code.slice(mk.s, mk.e) });
    cursor = mk.e;
  }
  if (cursor < code.length) pushPlain(code.slice(cursor));
  return parts;
}

const TOKEN_STYLE = {
  kw: { color: 'color-mix(in srgb, var(--trodo-primary) 78%, var(--text-primary))' },
  string: { color: 'var(--feedback-success)' },
  comment: { color: 'var(--text-disabled)', fontStyle: 'italic' },
  plain: { color: 'var(--text-secondary)' },
};

function CodeBlock({ step, title, code, lang }) {
  const [copied, setCopied] = useState(false);
  const tokens = useMemo(() => highlight(code, lang), [code, lang]);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) { /* clipboard blocked */ }
  };
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2">
        {step != null && (
          <span className="text-[11.5px] tabular-nums text-[color:var(--text-muted)]">{step}.</span>
        )}
        <span className="text-[11.5px] font-medium text-[color:var(--text-primary)]">{title}</span>
        <div className="flex-1" />
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1 text-[11px] text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)]"
        >
          {copied ? <Check size={11} /> : <Copy size={11} />} {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className="overflow-hidden border-y border-[color:var(--border-subtle)]">
        <pre className="overflow-x-auto px-3 py-2.5 font-mono text-[11.5px] leading-relaxed">
          {tokens.map((t, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <span key={i} style={TOKEN_STYLE[t.t] || TOKEN_STYLE.plain}>{t.v}</span>
          ))}
        </pre>
      </div>
    </div>
  );
}

// ── snippet generation ───────────────────────────────────────────────────────

function selectorFor(selector, lang) {
  const label = selector?.kind === 'hash' ? null : (selector?.value || 'production');
  if (lang === 'py') {
    return label ? { arg: `label="${label}"`, why: `follows the \`${label}\` label` }
      : { arg: `version="${selector.value}"`, why: 'pins this exact version' };
  }
  return label ? { arg: `{ label: '${label}' }`, why: `follows the \`${label}\` label` }
    : { arg: `{ version: '${selector.value}' }`, why: 'pins this exact version' };
}

// Say what omitting each variable produces, since that's the only thing the
// caller can't read off the name.
function varNote(v) {
  if (v.type === 'messages') return 'prior turns';
  if (v.default != null && v.default !== '') return `omit it and you get ${JSON.stringify(String(v.default))}`;
  return 'omit it and it renders empty';
}

function varArgs(variables, lang) {
  if (!variables.length) return '';
  if (lang === 'py') {
    return `\n${variables.map((v) => (v.type === 'messages'
      ? `    ${v.name}=[],  # ${varNote(v)}`
      : `    ${v.name}="…",  # ${varNote(v)}`)).join('\n')}\n`;
  }
  return `{\n${variables.map((v) => (v.type === 'messages'
    ? `  ${v.name}: [], // ${varNote(v)}`
    : `  ${v.name}: '…', // ${varNote(v)}`)).join('\n')}\n}`;
}

/**
 * Two blocks, not a step list.
 *
 *   1. Setup   — install + init, the part you write once at start-up.
 *   2. Use it  — fetch, compile and hand off, in ONE block with comments,
 *                because that is how it lands in a codebase: a single function,
 *                not five snippets to stitch together.
 *
 * The hand-off stays GENERIC on purpose. Every provider's SDK differs — the
 * client's name, the method, whether the system prompt sits inside `messages`,
 * whether max_tokens is required — and guessing at it produces confident,
 * wrong code. `compile()` hands back the four pieces; which SDK call they go
 * into is the provider's documentation to answer, not ours.
 */
function buildBlocks({ name, variables, selector, model, tools, responseFormat, lang }) {
  const py = lang === 'py';
  const sel = selectorFor(selector, lang);
  const hasVars = variables.length > 0;
  const hasTools = (tools || []).length > 0;
  const hasModel = model && Object.keys(model).length > 0;
  const structured = responseFormat?.type === 'json_schema';
  const jsonMode = responseFormat?.type === 'json_object';

  const setup = py
    ? `# pip install trodo\n\nimport os\nimport trodo\n\ntrodo.init(site_id=os.environ["TRODO_SITE_ID"])`
    : `// npm install trodo-node\n\nimport trodo from 'trodo-node';\n\ntrodo.init({ siteId: process.env.TRODO_SITE_ID });`;

  const L = [];
  const c = py ? '#' : '//';

  L.push(`${c} 1. Fetch: ${sel.why}.`);
  L.push(py
    ? `prompt = trodo.get_prompt("${name}", ${sel.arg})`
    : `const prompt = await trodo.prompts.get('${name}', ${sel.arg});`);
  L.push('');

  L.push(hasVars
    ? `${c} 2. Compile: fills the ${variables.length} declared variable${variables.length === 1 ? '' : 's'}.`
    : `${c} 2. Compile: this prompt declares no variables.`);
  L.push(py
    ? `compiled = prompt.compile(${varArgs(variables, 'py')})`
    : `const compiled = prompt.compile(${varArgs(variables, 'js')});`);
  L.push('');

  // Spell out what came back, and only mention the parts this version sets.
  L.push(`${c} 3. compiled holds everything this version pins:`);
  L.push(`${c}    .messages          the rendered conversation`);
  L.push(hasModel
    ? `${c}    .model             ${JSON.stringify(model)}`
    : `${c}    .model             {}: this prompt pins no model, choose one in code`);
  L.push(hasTools
    ? `${c}    .tools             ${tools.length} definition${tools.length === 1 ? '' : 's'}, pass straight through`
    : `${c}    .tools             []`);
  L.push(structured
    ? `${c}    .response_format   a JSON schema the reply is constrained to`
    : jsonMode
      ? `${c}    .response_format   JSON mode`
      : `${c}    .response_format   None`);
  L.push('');

  L.push(`${c} 4. Hand them to your provider's SDK. The call shape differs per`);
  L.push(`${c}    provider, so follow their docs: or paste this with "Copy as`);
  L.push(`${c}    prompt" and let your editor's agent wire it up.`);
  L.push(py ? 'resp = call_your_model(compiled)' : 'const resp = await callYourModel(compiled);');

  return [
    { title: 'Set up, once', code: setup },
    { title: 'Use it', code: L.join('\n') },
  ];
}

/** The whole thing as instructions for a coding agent. */
function asAgentPrompt({ name, variables, selector, model, tools, responseFormat, blocks, lang }) {
  const sel = selector?.kind === 'hash'
    ? `pinned to version ${selector.value}`
    : `following the \`${selector?.value || 'production'}\` label`;
  const lines = [
    `Integrate the Trodo managed prompt \`${name}\` into this codebase.`,
    '',
    '## What this prompt is',
    `- Fetched from Trodo at run time, ${sel}. Do not copy the prompt text into the code: the point is that it can change without a deploy.`,
    `- Language: ${lang === 'py' ? 'Python' : 'Node.js / TypeScript'}.`,
  ];
  if (variables.length) {
    lines.push(`- Declared variables: ${variables.map((v) => `\`${v.name}\` (${v.type || 'string'}, ${varNote(v)})`).join('; ')}.`);
    lines.push('- No variable is mandatory: omitting one renders its default, or empty. `compile()` only fails if you pass a name the prompt does not declare.');
  } else {
    lines.push('- No variables: compile() takes no arguments.');
  }
  if (model && Object.keys(model).length) {
    lines.push(`- Model config travels with the prompt: ${JSON.stringify(model)}. Read it from compiled.model rather than hardcoding.`);
  } else {
    lines.push('- This prompt sets no model config; choose the model in code.');
  }
  if ((tools || []).length) {
    lines.push(`- Declares ${tools.length} tool(s). Pass compiled.tools to the provider and implement the handlers in this codebase; Trodo stores the definitions, not the logic.`);
  }
  if (responseFormat?.type === 'json_schema') {
    lines.push('- Uses a structured output schema. Pass compiled.response_format through and parse the reply as JSON.');
  } else if (responseFormat?.type === 'json_object') {
    lines.push('- Uses JSON mode. Pass compiled.response_format through; make sure a message mentions JSON or OpenAI rejects the call.');
  }
  lines.push('', '## Steps', '');
  blocks.forEach((b, i) => {
    lines.push(`### ${i + 1}. ${b.title}`, '', '```' + (lang === 'py' ? 'python' : 'javascript'), b.code, '```', '');
  });
  lines.push('## Notes');
  lines.push('- `TRODO_SITE_ID` must be present in the environment.');
  lines.push('- `compile()` returns `{ messages, model, tools, response_format }` — use all of it rather than just the messages.');
  lines.push('- The final call is left generic on purpose. Replace `callYourModel(compiled)` with the real SDK call for the provider above, using that provider\'s current documentation: not a remembered signature.');
  lines.push('- Follow the conventions already in this codebase for HTTP clients, config and error handling.');
  lines.push(`- Reference: ${DOCS_URL}`);
  return lines.join('\n');
}

const TABS = [['node', 'Node.js'], ['python', 'Python']];

export default function SdkCodeModal({
  name, variables, selector, model, tools, responseFormat, messages, onClose,
}) {
  const [tab, setTab] = useState('node');
  const [copiedPrompt, setCopiedPrompt] = useState(false);

  const lang = tab === 'python' ? 'py' : 'js';
  // Per-item section fields are DECLARED (that declaration is what makes an
  // item that omits them render empty, and their Default the per-item
  // fallback) — but they are not call-time arguments, and a snippet that lists
  // them teaches the caller to pass values that do nothing. Filter them from
  // every generated call.
  const inner = useMemo(() => sectionInnerNames(messages || []), [messages]);
  const vars = useMemo(
    () => (variables || []).filter((v) => !inner.has(v.name)),
    [variables, inner],
  );

  const blocks = useMemo(() => buildBlocks({
    name: name || 'prompt-name', variables: vars, selector, model, tools, responseFormat, lang,
  }), [name, vars, selector, model, tools, responseFormat, lang]);

  const copyAsPrompt = async () => {
    try {
      await navigator.clipboard.writeText(asAgentPrompt({
        name: name || 'prompt-name', variables: vars, selector, model, tools, responseFormat, blocks, lang,
      }));
      setCopiedPrompt(true);
      setTimeout(() => setCopiedPrompt(false), 1800);
    } catch (_) { /* clipboard blocked */ }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div
        className="flex max-h-[90vh] w-[680px] max-w-[95vw] flex-col overflow-hidden rounded-xl border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        <div className="flex items-center gap-2 border-b border-[color:var(--border-subtle)] px-4 py-2.5">
          <h2 className="text-[13.5px] font-semibold text-[color:var(--text-primary)]">Use this prompt</h2>
          <div className="flex-1" />
          <button type="button" onClick={onClose} aria-label="Close" title="Close" className={drawerIconButtonClass}><X size={15} /></button>
        </div>

        <div className="flex items-center gap-1 border-b border-[color:var(--border-subtle)] px-3">
          {TABS.map(([key, label]) => (
            <DrawerTab key={key} id={key} label={label} active={tab === key} onClick={setTab} />
          ))}
          <div className="flex-1" />
          {/* For people who'd rather hand the whole integration to their editor
              agent than type it out. */}
          <button
            type="button"
            onClick={copyAsPrompt}
            title="Copy every step as instructions for Cursor, Claude Code or similar"
            className="my-1 inline-flex h-7 items-center gap-1.5 rounded-md border border-[color:var(--border-default)] bg-surface px-2.5 text-[12px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover"
          >
            {copiedPrompt ? <Check size={12} /> : null}
            {copiedPrompt ? 'Copied' : 'Copy as prompt'}
          </button>
        </div>

        <div className="flex flex-col gap-4 overflow-y-auto px-4 py-4">
          {blocks.map((b, i) => (
            <CodeBlock key={b.title} step={i + 1} title={b.title} code={b.code} lang={lang} />
          ))}
        </div>

        <div className="flex shrink-0 items-center gap-1.5 border-t border-[color:var(--border-subtle)] px-4 py-2.5 text-[11.5px] text-[color:var(--text-muted)]">
          <span>Everything else</span>
          <a
            href={DOCS_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 font-medium text-[color:var(--text-primary)] underline hover:opacity-80"
          >
            Prompts quickstart <ExternalLink size={11} />
          </a>
        </div>
      </div>
    </div>
  );
}
